/**
 * /fiches/eleve/livret-progression-plateau-a2 — Landing ÉLÈVE
 * Livret de progression avec Pourquoi/Comment/Risque + cases.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, Download, Printer, Bookmark, BookmarkCheck,
  Share2, CheckCircle2, ListChecks, Trophy, Sparkles, ChevronRight, HelpCircle, Wrench, AlertTriangle,
} from 'lucide-react';

const RESOURCE_KEY = 'code2roues:eleve:ressources';
const RESOURCE_ID = 'livret-progression-plateau-a2';

const FICHE_META = {
  id: RESOURCE_ID,
  title: 'Livret de progression — Plateau A2',
  subtitle: 'Coche à ton rythme, comme dans ton livret',
  category: 'Moto A2 · Plateau',
  ficheUrl: '/fiches/moto/livret-progression-plateau-a2',
};

function getSavedResources() {
  try { return JSON.parse(localStorage.getItem(RESOURCE_KEY) || '[]'); }
  catch (_) { return []; }
}
function saveResource(meta) {
  const list = getSavedResources();
  if (list.find((r) => r.id === meta.id)) return list;
  const next = [...list, { ...meta, savedAt: new Date().toISOString() }];
  localStorage.setItem(RESOURCE_KEY, JSON.stringify(next));
  return next;
}
function removeResource(id) {
  const next = getSavedResources().filter((r) => r.id !== id);
  localStorage.setItem(RESOURCE_KEY, JSON.stringify(next));
  return next;
}

export default function LivretProgressionEleveLanding() {
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(false);
  const [showToast, setShowToast] = useState(null);

  useEffect(() => {
    document.title = 'Mon livret plateau A2 · code2roues';
    setIsSaved(!!getSavedResources().find((r) => r.id === RESOURCE_ID));
  }, []);

  const toast = (msg) => {
    setShowToast(msg);
    setTimeout(() => setShowToast(null), 2500);
  };

  const handleSave = () => {
    if (isSaved) {
      removeResource(RESOURCE_ID);
      setIsSaved(false);
      toast('Livret retiré de mes ressources');
    } else {
      saveResource(FICHE_META);
      setIsSaved(true);
      toast('✅ Livret enregistré dans mes ressources');
    }
  };

  const handleDownloadPDF = () => {
    const w = window.open(FICHE_META.ficheUrl, '_blank');
    if (w) {
      w.addEventListener('load', () => {
        setTimeout(() => w.print(), 800);
      });
    }
  };

  const handlePrint = () => {
    window.open(FICHE_META.ficheUrl + '?autoprint=1', '_blank');
  };

  const handleShare = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (navigator.share) {
      try { await navigator.share({ title: FICHE_META.title, text: 'Mon livret plateau A2 — code2roues', url }); }
      catch (_) { /* cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast('🔗 Lien copié');
      } catch (_) { toast('Copie impossible'); }
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white pb-24">
      <div className="sticky top-0 z-20 bg-neutral-950/90 backdrop-blur border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-[13px] font-semibold"
            data-testid="btn-back"
          >
            <ArrowLeft size={14} /> Retour
          </button>
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-violet-400">code2roues.fr</div>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-2xl mx-auto px-4 pt-6">
        <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-violet-600/25 via-violet-500/10 to-transparent p-6">
          <div className="absolute top-4 right-4">
            <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest bg-violet-600 text-white px-2 py-1 rounded-full">
              <Sparkles size={10} /> Ton livret
            </span>
          </div>
          <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-violet-400 mb-2">
            <ListChecks size={11} /> Progression jalonnée
          </div>
          <h1 className="text-[24px] sm:text-[28px] font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {FICHE_META.title}
          </h1>
          <p className="text-white/70 text-[13px] mt-2 leading-relaxed">
            Pour chaque compétence à acquérir : le <strong className="text-sky-300">POURQUOI</strong> (à quoi ça sert), le <strong className="text-emerald-300">COMMENT</strong> (la technique) et le <strong className="text-rose-300">RISQUE</strong> (l&apos;erreur à éviter). Tu coches <strong>Découvert / En cours / Acquis</strong> à <strong>ton rythme</strong>, avec ton moniteur.
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-4 text-[11px] text-white/60">
            <span className="inline-flex items-center gap-1 bg-white/[0.06] border border-white/10 px-2 py-1 rounded-full">
              <Trophy size={11} /> 19 compétences
            </span>
            <span className="inline-flex items-center gap-1 bg-white/[0.06] border border-white/10 px-2 py-1 rounded-full">
              <CheckCircle2 size={11} /> 4 niveaux
            </span>
            <span className="inline-flex items-center gap-1 bg-white/[0.06] border border-white/10 px-2 py-1 rounded-full">
              <ListChecks size={11} /> Guide + Livret
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="max-w-2xl mx-auto px-4 mt-5">
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/40 mb-2">Que veux-tu faire ?</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <ActionCard
            icon={BookOpen}
            title="Consulter le livret complet"
            desc="14 compétences avec Pourquoi/Comment/Risque + cases."
            onClick={() => window.open(FICHE_META.ficheUrl, '_blank')}
            variant="primary"
            testId="action-consulter"
          />
          <ActionCard
            icon={isSaved ? BookmarkCheck : Bookmark}
            title={isSaved ? 'Enregistré ✓' : 'Sauvegarder mon livret'}
            desc={isSaved ? 'Accessible à tout moment sur cet appareil.' : 'Garde-le même hors connexion.'}
            onClick={handleSave}
            variant={isSaved ? 'success' : 'default'}
            testId="action-sauvegarder"
          />
          <ActionCard
            icon={Download}
            title="Télécharger en PDF"
            desc="Impression → « Enregistrer au format PDF »."
            onClick={handleDownloadPDF}
            variant="default"
            testId="action-telecharger"
          />
          <ActionCard
            icon={Printer}
            title="Imprimer"
            desc="Format A4 pour ton classeur d’apprentissage."
            onClick={handlePrint}
            variant="default"
            testId="action-imprimer"
          />
        </div>

        <button
          onClick={handleShare}
          className="mt-2.5 w-full inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-white/80 text-[13px] font-semibold transition"
          data-testid="action-partager"
        >
          <Share2 size={14} /> Partager avec un(e) camarade
        </button>
      </div>

      {/* Comment ça marche */}
      <div className="max-w-2xl mx-auto px-4 mt-8">
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/40 mb-2">Comment ce livret est structuré</div>
        <div className="grid grid-cols-1 gap-2">
          <StructureCard icon={HelpCircle} color="#0ea5e9" label="POURQUOI" desc="L&apos;objectif de la compétence : à quoi elle sert dans le plateau et dans la vraie vie de motard." />
          <StructureCard icon={Wrench} color="#10b981" label="COMMENT" desc="La technique à appliquer : les gestes, la position, les repères à utiliser." />
          <StructureCard icon={AlertTriangle} color="#dc2626" label="RISQUE" desc="L&apos;erreur classique à éviter et sa conséquence (chute, note C, calage…)." />
        </div>
      </div>

      {/* Les 4 niveaux */}
      <div className="max-w-2xl mx-auto px-4 mt-8">
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/40 mb-2">Les 4 niveaux du livret</div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
          <NiveauRow n="0" title="Théorie fondamentale — Guide 2-roues" count="5 compétences" desc="Regard · Position · Mécanique · Lois physiques · EPI" color="#a855f7" />
          <NiveauRow n="1" title="Fondamentaux — Prise en main" count="5 compétences" desc="Équilibre, commandes, démarrage, patinage, roulage droit" color="#8b5cf6" />
          <NiveauRow n="2" title="Les 6 manœuvres officielles" count="6 compétences" desc="M1 sans moteur → M6 évitement (source Sécurité Routière)" color="#6366f1" />
          <NiveauRow n="3" title="Maîtrise pour l&apos;examen" count="3 compétences" desc="Enchaînement + gestion du stress + examens blancs" color="#4f46e5" last />
        </div>
      </div>

      {/* Message pour l'élève */}
      <div className="max-w-2xl mx-auto px-4 mt-8">
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-400 mb-1">💡 Le mot du moniteur</div>
          <p className="text-white/80 text-[13px] leading-relaxed">
            <strong>Chaque motard progresse à son rythme.</strong> Certain(e)s valident une compétence en 1 leçon, d&apos;autres en 3. C&apos;est normal, ce n&apos;est <strong>pas une course</strong>. L&apos;important est de <strong>valider</strong> chaque étape avant de passer à la suivante — c&apos;est ce qui te rendra <strong>sûr(e) de toi le jour J</strong>. Le <strong>Niveau 0 (théorie)</strong> est à lire dès la 1<sup>ʳᵉ</sup> leçon, il t&apos;évitera 90% des erreurs de débutant.
          </p>
        </div>
      </div>

      {showToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white text-neutral-900 px-4 py-2.5 rounded-full shadow-2xl text-[13px] font-semibold" data-testid="toast">
          {showToast}
        </div>
      )}
    </div>
  );
}

function ActionCard({ icon: Icon, title, desc, onClick, variant = 'default', testId }) {
  const styles = {
    primary: 'border-violet-500/40 bg-violet-500/10 hover:bg-violet-500/20 text-white',
    success: 'border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-white',
    default: 'border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-white',
  }[variant];

  return (
    <button
      onClick={onClick}
      className={`text-left rounded-xl border p-4 transition group ${styles}`}
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-white/[0.06] border border-white/10 flex items-center justify-center shrink-0 group-hover:scale-110 transition">
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-black leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {title}
          </div>
          <div className="text-[11.5px] text-white/60 mt-1 leading-snug">{desc}</div>
        </div>
      </div>
    </button>
  );
}

function StructureCard({ icon: Icon, color, label, desc }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl border border-white/10 bg-white/[0.03]">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}25`, color }}>
        <Icon size={17} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-black uppercase tracking-widest" style={{ color }}>{label}</div>
        <div className="text-white/70 text-[12.5px] leading-snug mt-0.5">{desc}</div>
      </div>
    </div>
  );
}

function NiveauRow({ n, title, count, desc, color, last }) {
  return (
    <div className={`flex items-center gap-3 px-4 py-3 ${last ? '' : 'border-b border-white/5'}`}>
      <span
        className="inline-flex items-center justify-center w-8 h-8 rounded-full text-white text-[13px] font-black shrink-0"
        style={{ background: color }}
      >
        {n}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-white text-[13px] font-bold leading-tight">{title}</div>
        <div className="text-white/50 text-[11px] mt-0.5">{count} · {desc}</div>
      </div>
      <ChevronRight size={14} className="text-white/30" />
    </div>
  );
}
